import SuppliesInventory from '../components/SuppliesInventory';
import React, { useState, useEffect } from 'react';
import { get, post, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import PhysicalStockCountPrintModal from '../components/PhysicalStockCountPrintModal';
import FinishedGoodsReleaseQueue from '../components/FinishedGoodsReleaseQueue';
const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');

export default function Inventory() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const matches = row => search.trim().toLowerCase().split(/\s+/).every(word => Object.values(row).filter(v => typeof v === 'string' || typeof v === 'number').join(' ').toLowerCase().includes(word));
  const [view, setView] = useState('valuation');
  const [valuation, setValuation] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [issuances, setIssuances] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdjust, setShowAdjust] = useState(false);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [showReceiptForm, setShowReceiptForm] = useState(false);
  const [showPhysicalCountPrint, setShowPhysicalCountPrint] = useState(false);
  const [issueForm, setIssueForm] = useState({ material_id:'', quantity:'', purpose:'', issued_to:'', department:'', notes:'', date:new Date().toISOString().split('T')[0] });
  const [receiptForm, setReceiptForm] = useState({ material_id:'', quantity:'', unit_cost:'', supplier_name:'', supplier_id:'', reference:'', notes:'', date:new Date().toISOString().split('T')[0] });
  const [createBill, setCreateBill] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  const shownTransactions = transactions.filter(matches);
  const shownIssuances = issuances.filter(matches);
  const shownReceipts = receipts.filter(matches);

  const PURPOSES = ['Admin use','Office supplies','Cleaning/maintenance','Samples','Photography','Events','Other'];

  const load = () => {
    setLoading(true);
    if (view==='supplies'||view==='finished_goods_release') {
      setLoading(false);
    } else if (view==='valuation') {
      get('/inventory/valuation').then(setValuation).finally(() => setLoading(false));
    } else if (view==='transactions') {
      get('/inventory/transactions').then(d => setTransactions(d.data||[])).finally(() => setLoading(false));
    } else if (view==='issuances') {
      Promise.all([get('/inventory/issuances'), get('/materials')]).then(([iss, mats]) => {
        setIssuances(iss.data||[]);
        setMaterials(mats.data||[]);
      }).finally(() => setLoading(false));
    } else if (view==='receipts') {
      Promise.all([get('/inventory/receipts'), get('/materials'), get('/suppliers')]).then(([rec, mats, sups]) => {
        setSuppliers(sups.data||[]);
        setReceipts(rec.data||[]);
        setMaterials(mats.data||[]);
      }).finally(() => setLoading(false));
    }
  };

  useEffect(() => { load(); }, [view]);

  const submitIssuance = async () => {
    if (!issueForm.material_id || !issueForm.quantity || !issueForm.purpose) {
      alert('Material, quantity and purpose are required'); return;
    }
    setSaving(true);
    try {
      await post('/inventory/issuances', issueForm);
      setShowIssueForm(false);
      setIssueForm({ material_id:'', quantity:'', purpose:'', issued_to:'', department:'', notes:'', date:new Date().toISOString().split('T')[0] });
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const submitReceipt = async () => {
    if (createBill && !receiptForm.supplier_id) {
      alert('Please select a supplier to create a bill.');
      return;
    }
    if (!receiptForm.material_id || !receiptForm.quantity) {
      alert('Material and quantity are required'); return;
    }
    setSaving(true);
    try {
      await post('/inventory/receipts', {...receiptForm, create_bill: createBill});
      setShowReceiptForm(false);
      setReceiptForm({ material_id:'', quantity:'', unit_cost:'', supplier_name:'', supplier_id:'', reference:'', notes:'', date:new Date().toISOString().split('T')[0] });
      setCreateBill(false);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const deleteIssuance = async (id) => {
    if (!window.confirm('Delete this issuance? Stock will be restored.')) return;
    try { await del('/inventory/issuances/'+id); load(); }
    catch(err) { alert(err.message); }
  };

  const deleteReceipt = async (id) => {
    if (!window.confirm('Delete this receipt? Stock will be reversed.')) return;
    try { await del('/inventory/receipts/'+id); load(); }
    catch(err) { alert(err.message); }
  };

  return (
    <>
    {showAdjust && (
      <StockAdjustmentModal
        onClose={() => setShowAdjust(false)}
        onSaved={() => { setShowAdjust(false); load(); }} />
    )}
    {showPhysicalCountPrint && (
      <PhysicalStockCountPrintModal
        generatedBy={
          user?.name ||
          user?.full_name ||
          user?.email ||
          'BloansBooks User'
        }
        onClose={() =>
          setShowPhysicalCountPrint(false)
        }
      />
    )}
    <div>
      <div className="flex-between mb-lg">
        <h2 className="page-title">Stock Ledger</h2>
        <div className="flex-center gap-sm">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() =>
              setShowPhysicalCountPrint(true)
            }
          >
            Print Physical Count Sheet
          </button>
          {[
            'super_admin',
            'inventory_officer'
          ].includes(user?.role) && (
            <button
              type="button"
              className={
                'btn btn-sm ' +
                (view === 'finished_goods_release'
                  ? 'btn-primary'
                  : 'btn-outline')
              }
              onClick={() =>
                setView('finished_goods_release')
              }
            >
              Finished Goods Release Queue
            </button>
          )}
          <button className={'btn btn-sm '+(view==='supplies'?'btn-primary':'btn-outline')} onClick={()=>setView('supplies')}>Supplies, Spares &amp; Maintenance</button>
          <button className={'btn btn-sm '+(view==='valuation'?'btn-primary':'btn-outline')} onClick={() => setView('valuation')}>Valuation</button>
          <button className={'btn btn-sm '+(view==='transactions'?'btn-primary':'btn-outline')} onClick={() => setView('transactions')}>Transactions</button>
        </div>
      </div>
      {view!=='supplies' && view!=='finished_goods_release' && <div className="mb-lg"><label htmlFor="stock-ledger-search">Search stock ledger</label><div style={{display:'flex',gap:8}}><input id="stock-ledger-search" type="search" className="form-control" placeholder="Search items, units, references or transaction types" value={search} onChange={e=>setSearch(e.target.value)}/>{search&&<button type="button" className="btn btn-outline" onClick={()=>setSearch('')}>Clear</button>}</div><small>Filters the displayed rows. Valuation totals remain for all stock.</small></div>}
      {loading ? <div className="spinner"></div> : view==='supplies' ? (<SuppliesInventory />) : view==='finished_goods_release' ? (
        <FinishedGoodsReleaseQueue />
      ) : view==='valuation' ? (
        <div>
          {valuation && (
            <div className="metrics" style={{gridTemplateColumns:'repeat(2,1fr)'}}>
              <div className="tile"><div className="tile-lbl">Supplies &amp; Spares Value</div><div className="tile-val">{fmt(valuation.totals.supplies_value)}</div></div>
              <div className="tile"><div className="tile-lbl">Production Materials Value</div><div className="tile-val">{fmt(valuation.totals.materials_value)}</div></div>
              <div className="tile"><div className="tile-lbl">Finished Goods Value</div><div className="tile-val">{fmt(valuation.totals.products_value)}</div></div>
            </div>
          )}
          <div className="card mb-lg">
            <div className="card-hdr"><span className="card-title">Raw Materials</span></div>
            <table>
              <thead><tr><th>Material</th><th>Unit</th><th style={{textAlign:'right'}}>Stock</th><th style={{textAlign:'right'}}>Avg Cost</th><th style={{textAlign:'right'}}>Value</th><th>Status</th></tr></thead>
              <tbody>
                {valuation?.materials.filter(matches).map(m => (
                  <tr key={m.name} className={m.is_low_stock?'low-stock-row':''}>
                    <td style={{fontWeight:500}}>{m.name}</td><td>{m.unit_of_measure}</td>
                    <td className="amt">{Number(m.current_stock).toLocaleString()}</td>
                    <td className="amt">{fmt(m.weighted_avg_cost)}</td>
                    <td className="amt">{fmt(m.total_value)}</td>
                    <td><span className={'badge '+(m.is_low_stock?'b-part':'b-paid')}>{m.is_low_stock?'Low Stock':'OK'}</span></td>
                  </tr>
                ))}
                {valuation && valuation.materials.filter(matches).length===0 && <tr><td colSpan={6}>{search.trim()?'No matching items':'No stock items'}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="card">
            <div className="card-hdr"><span className="card-title">Finished Goods</span></div>
            <table className="tbl">
              <thead><tr style={{background:'#556B2F'}}><th style={{color:'#F2EFE7'}}>Product</th><th style={{color:'#F2EFE7'}}>Size</th><th style={{textAlign:'right',color:'#F2EFE7'}}>Stock</th><th style={{textAlign:'right',color:'#F2EFE7'}}>Price</th><th style={{textAlign:'right',color:'#F2EFE7'}}>Value</th><th style={{color:'#F2EFE7'}}>Status</th></tr></thead>
              <tbody>
                {valuation?.products.filter(matches).map(p => (
                  <tr key={p.name} className={p.is_low_stock?'low-stock-row':''}>
                    <td style={{fontWeight:500}}>{p.name}</td><td>{p.size||'—'}</td>
                    <td className="amt">{Number(p.current_stock).toLocaleString()}</td>
                    <td className="amt">{fmt(p.selling_price)}</td>
                    <td className="amt">{fmt(p.total_value)}</td>
                    <td><span className={'badge '+(p.is_low_stock?'b-part':'b-paid')}>{p.is_low_stock?'Low Stock':'OK'}</span></td>
                  </tr>
                ))}
                {valuation && valuation.products.filter(matches).length===0 && <tr><td colSpan={6}>{search.trim()?'No matching items':'No stock items'}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr style={{background:'#556B2F'}}><th style={{color:'#F2EFE7'}}>Ref</th><th style={{color:'#F2EFE7'}}>Date</th><th style={{color:'#F2EFE7'}}>Type</th><th style={{color:'#F2EFE7'}}>Item</th><th style={{textAlign:'right',color:'#F2EFE7'}}>Qty</th><th style={{textAlign:'right',color:'#F2EFE7'}}>Unit Cost</th></tr></thead>
            <tbody>
              {shownTransactions.length === 0
                ? <tr><td colSpan="6"><div className="empty-state"><h3>{search.trim()?'No matching transactions':'No transactions yet'}</h3></div></td></tr>
                : shownTransactions.map(t => (
                  <tr key={t.id}>
                    <td>{t.txn_ref}</td><td>{t.date}</td>
                    <td><span className="badge b-grey">{(t.type||'').replace('_',' ')}</span></td>
                    <td>{t.item_type}</td>
                    <td className="amt">{Number(t.quantity).toLocaleString()}</td>
                    <td className="amt">{t.unit_cost?fmt(t.unit_cost):'—'}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}
      {/* ISSUANCES VIEW */}
      {view==='issuances' && !loading && (
        <div>
          <div className="flex-between" style={{marginBottom:16}}>
            <div style={{fontSize:13,color:'#8A8A8A'}}>{issuances.length} issuance records</div>
            <button className="btn btn-primary btn-sm" onClick={()=>{setShowIssueForm(true); get('/materials').then(d=>setMaterials(d.data||[])); }}>
              + Issue Materials
            </button>
          </div>

          {showIssueForm && (
            <div style={{background:'#F2EFE7',borderRadius:10,padding:20,marginBottom:20,border:'1px solid var(--border-light)'}}>
              <div style={{fontSize:14,fontWeight:600,color:'#3B2A21',marginBottom:16}}>Issue Materials (General)</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Material *</label>
                  <select className="form-control" value={issueForm.material_id}
                    onChange={e=>setIssueForm({...issueForm,material_id:e.target.value})}>
                    <option value="">Select material...</option>
                    {materials.map(m=><option key={m.id} value={m.id}>{m.name} (Stock: {m.current_stock} {m.unit_of_measure})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Quantity *</label>
                  <input type="number" className="form-control" value={issueForm.quantity}
                    onChange={e=>setIssueForm({...issueForm,quantity:e.target.value})}
                    placeholder="0" min="0.01" step="0.01" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Purpose *</label>
                  <select className="form-control" value={issueForm.purpose}
                    onChange={e=>setIssueForm({...issueForm,purpose:e.target.value})}>
                    <option value="">Select purpose...</option>
                    {PURPOSES.map(p=><option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input type="date" className="form-control" value={issueForm.date}
                    onChange={e=>setIssueForm({...issueForm,date:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Issued To</label>
                  <input className="form-control" value={issueForm.issued_to}
                    onChange={e=>setIssueForm({...issueForm,issued_to:e.target.value})}
                    placeholder="Staff name or department" />
                </div>
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <input className="form-control" value={issueForm.department}
                    onChange={e=>setIssueForm({...issueForm,department:e.target.value})}
                    placeholder="e.g. Admin, Production" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-control" value={issueForm.notes}
                  onChange={e=>setIssueForm({...issueForm,notes:e.target.value})}
                  placeholder="Additional notes..." />
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-outline btn-sm" onClick={()=>setShowIssueForm(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={submitIssuance} disabled={saving}>
                  {saving?'Saving...':'Issue Materials'}
                </button>
              </div>
            </div>
          )}

          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Date','Material','Quantity','Purpose','Issued To','Dept','Issued By',''].map(h=>(
                <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {shownIssuances.length===0 ? (
                <tr><td colSpan={8} style={{padding:20,textAlign:'center',color:'#8A8A8A'}}>{search.trim()?'No matching issuances':'No issuances recorded yet'}</td></tr>
              ) : shownIssuances.map((iss,i)=>(
                <tr key={iss.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{iss.date}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{iss.material_name}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{iss.quantity} {iss.unit_of_measure}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{iss.purpose}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{iss.issued_to||'—'}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{iss.department||'—'}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>{iss.issued_by_name}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                    {user?.role==='super_admin' && (
                      <button className="btn btn-danger btn-sm" style={{fontSize:10}}
                        onClick={()=>deleteIssuance(iss.id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* RECEIPTS VIEW */}
      {view==='receipts' && !loading && (
        <div>
          <div className="flex-between" style={{marginBottom:16}}>
            <div style={{fontSize:13,color:'#8A8A8A'}}>{receipts.length} receipt records</div>
            <button className="btn btn-primary btn-sm" onClick={()=>{setShowReceiptForm(true); get('/materials').then(d=>setMaterials(d.data||[])); }}>
              + Receive Stock
            </button>
          </div>

          {showReceiptForm && (
            <div style={{background:'#F2EFE7',borderRadius:10,padding:20,marginBottom:20,border:'1px solid var(--border-light)'}}>
              <div style={{fontSize:14,fontWeight:600,color:'#3B2A21',marginBottom:16}}>Direct Stock Receipt (No PO)</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Material *</label>
                  <select className="form-control" value={receiptForm.material_id}
                    onChange={e=>setReceiptForm({...receiptForm,material_id:e.target.value})}>
                    <option value="">Select material...</option>
                    {materials.map(m=><option key={m.id} value={m.id}>{m.name} (Stock: {m.current_stock} {m.unit_of_measure})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Quantity *</label>
                  <input type="number" className="form-control" value={receiptForm.quantity}
                    onChange={e=>setReceiptForm({...receiptForm,quantity:e.target.value})}
                    placeholder="0" min="0.01" step="0.01" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Supplier Name</label>
                  <input className="form-control" value={receiptForm.supplier_name}
                    onChange={e=>setReceiptForm({...receiptForm,supplier_name:e.target.value})}
                    placeholder="Supplier or vendor name (free text)" />
                </div>
                <div className="form-group">
                  <label className="form-label">Unit Cost (₦)</label>
                  <input type="number" className="form-control" value={receiptForm.unit_cost}
                    onChange={e=>setReceiptForm({...receiptForm,unit_cost:e.target.value})}
                    placeholder="0.00" min="0" step="0.01" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Reference / Receipt No</label>
                  <input className="form-control" value={receiptForm.reference}
                    onChange={e=>setReceiptForm({...receiptForm,reference:e.target.value})}
                    placeholder="Receipt or waybill number" />
                </div>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input type="date" className="form-control" value={receiptForm.date}
                    onChange={e=>setReceiptForm({...receiptForm,date:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-control" value={receiptForm.notes}
                  onChange={e=>setReceiptForm({...receiptForm,notes:e.target.value})}
                  placeholder="Any additional notes..." />
              </div>
              <div style={{background:'#F9F8F5',borderRadius:8,padding:12,marginBottom:12,border:'1px solid #E8E0D4'}}>
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,fontWeight:600,color:'#3B2A21'}}>
                  <input type="checkbox" checked={createBill} onChange={e=>setCreateBill(e.target.checked)} style={{width:16,height:16}} />
                  Auto-create a Supplier Bill for this receipt
                </label>
                <div style={{fontSize:11,color:'#8A8A8A',marginTop:6}}>
                  {createBill ? '✅ A bill will be created and linked to this supplier.' : '💡 Tick this to automatically create a bill when stock is received.'}
                </div>
                {createBill && (
                  <div className="form-group" style={{marginTop:10}}>
                    <label className="form-label">Select Supplier *</label>
                    <select className="form-control" value={receiptForm.supplier_id}
                      onChange={e=>setReceiptForm({...receiptForm,supplier_id:e.target.value})}>
                      <option value="">Select supplier...</option>
                      {suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-outline btn-sm" onClick={()=>setShowReceiptForm(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={submitReceipt} disabled={saving}>
                  {saving?'Saving...':'Receive Stock'}
                </button>
              </div>
            </div>
          )}

          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Date','Material','Quantity','Supplier','Unit Cost','Reference','Received By',''].map(h=>(
                <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {shownReceipts.length===0 ? (
                <tr><td colSpan={8} style={{padding:20,textAlign:'center',color:'#8A8A8A'}}>{search.trim()?'No matching receipts':'No receipts recorded yet'}</td></tr>
              ) : shownReceipts.map((rec,i)=>(
                <tr key={rec.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{rec.date}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{rec.material_name}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{rec.quantity} {rec.unit_of_measure}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{rec.supplier_name||'—'}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                    {rec.unit_cost > 0 ? '₦'+Number(rec.unit_cost).toLocaleString() : '—'}
                  </td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{rec.reference||'—'}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>{rec.received_by_name}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                    {user?.role==='super_admin' && (
                      <button className="btn btn-danger btn-sm" style={{fontSize:10}}
                        onClick={()=>deleteReceipt(rec.id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </>
  );
}
